import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import PilotsPanel from "./PilotsPanel";
import AircraftPanel from "./AircraftPanel";
import EmailPanel from "./EmailPanel";
import DiAuditPanel from "./DiAuditPanel";
import ReservationsPanel from "./di/ReservationsPanel";
// Sub-components extracted to di/ folder
import InstructorForm from "./di/InstructorForm";
import FlightLogDialog from "./di/FlightLogDialog";
import AlertsBanner, { AuditLogDialog } from "./di/AlertsBanner";
import InstructorDashboardDialog from "./di/InstructorDashboardDialog";
import InstructorCard from "./di/InstructorCard";
import { todayStr } from "./di/diUtils";
import { Plane, Plus, Download, Calendar, Mail, CalendarCheck } from "lucide-react";
import { API, useAuth } from "../../App";

// Re-export FlightForm so external consumers (e.g. AircraftPanel) keep working
export { default as FlightForm } from "./di/FlightForm";

export default function DiPanel() {
  const { school } = useAuth();
  // Fleet-only customers manage aircraft first: open there, not on an empty instructors list.
  const [subTab, setSubTab] = useState(null);
  const activeSub = subTab ?? (school?.product === "fleet" ? "aircraft" : "instructors");
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openLog, setOpenLog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [auditFor, setAuditFor] = useState(null);
  const [dashFor, setDashFor] = useState(null);
  const [alertsRefreshKey, setAlertsRefreshKey] = useState(0);
  const [asOf, setAsOf] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  // Fichas fechadas por defeito (frase de estado e condições à vista); abre-se a que se quiser ver
  const [openIds, setOpenIds] = useState(() => new Set());
  // Documentos por instrutor (o cartão só mostra o botão quando há algum)
  const [docCounts, setDocCounts] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (asOf) params.as_of = asOf;
      if (showInactive) params.include_inactive = "true";
      const r = await axios.get(`${API}/di/instructors`, { withCredentials: true, params });
      setInstructors(r.data || []);
      // Se a contagem falhar, a lista continua, só sem o botão dos documentos
      axios.get(`${API}/di/documents/counts/instructor`, { withCredentials: true })
        .then((c) => setDocCounts(c.data || {}))
        .catch(() => setDocCounts({}));
      setAlertsRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  }, [asOf, showInactive]);

  useEffect(() => { refresh(); }, [refresh]);

  const removeInstructor = async (inst) => {
    if (!window.confirm(`Eliminar instrutor ${inst.name}? Os voos associados também serão apagados.`)) return;
    try {
      await axios.delete(`${API}/di/instructors/${inst.id}`, { withCredentials: true });
      toast.success("Instrutor eliminado");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a eliminar");
    }
  };

  const demoteToInstructor = async (instructorId) => {
    try {
      const r = await axios.post(`${API}/di/instructors/${instructorId}/demote-to-pilot`, {}, { withCredentials: true });
      const d = r.data;
      if (d.created_new_pilot) {
        toast.success("Instrutor despromovidoo a Piloto — nova ficha de piloto criada e instrutor arquivado.");
      } else {
        toast.success("Instrutor despromovidoo a Piloto — ficha arquivada, piloto original reactivado.");
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a despromover");
    }
  };

  const toggleInstructorActive = async (inst) => {
    const next = !(inst.active !== false);
    try {
      await axios.patch(`${API}/di/instructors/${inst.id}/active`, { active: next }, { withCredentials: true });
      toast.success(next ? "Instrutor reactivado" : "Instrutor desactivado");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a actualizar estado");
    }
  };

  const counts = instructors.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});

  const downloadCsv = async () => {
    try {
      const params = asOf ? { as_of: asOf } : {};
      const r = await axios.get(`${API}/di/export/instructors.csv`, { withCredentials: true, params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `di_instrutores_${asOf || new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch (e) {
      toast.error("Erro ao exportar CSV");
    }
  };

  return (
    <div className="space-y-4" data-testid="di-panel">
      <Tabs value={activeSub} onValueChange={setSubTab} className="w-full">
        <TabsList className="bg-slate-900/60 border border-cockpit-border grid grid-cols-6 w-full sm:inline-flex sm:w-auto h-auto sm:h-9">
          <TabsTrigger value="instructors" data-testid="di-subtab-instructors" className="text-[11px] sm:text-sm px-1.5 sm:px-3">
            <Plane className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Instrutores</span><span className="sm:hidden ml-1">Instr.</span>
          </TabsTrigger>
          <TabsTrigger value="pilots" data-testid="di-subtab-pilots" className="text-[11px] sm:text-sm px-1.5 sm:px-3">
            <Plane className="w-4 h-4 sm:mr-1.5 rotate-45" /> <span className="hidden sm:inline">Pilotos</span><span className="sm:hidden ml-1">Pil.</span>
          </TabsTrigger>
          <TabsTrigger value="aircraft" data-testid="di-subtab-aircraft" className="text-[11px] sm:text-sm px-1.5 sm:px-3">
            <Plane className="w-4 h-4 sm:mr-1.5 -rotate-12" /> <span className="hidden sm:inline">Aeronaves</span><span className="sm:hidden ml-1">Aer.</span>
          </TabsTrigger>
          <TabsTrigger value="reservations" data-testid="di-subtab-reservations" className="text-[11px] sm:text-sm px-1.5 sm:px-3">
            <CalendarCheck className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Reservas</span><span className="sm:hidden ml-1">Res.</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="di-subtab-notifications" className="text-[11px] sm:text-sm px-1.5 sm:px-3">
            <Mail className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Notificações</span><span className="sm:hidden ml-1">Notif.</span>
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="di-subtab-audit" className="text-[11px] sm:text-sm px-1.5 sm:px-3">
            <span className="sm:hidden ml-1">Audit.</span><span className="hidden sm:inline">Auditoria</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="instructors" className="space-y-4 mt-3">
          <AlertsBanner
            key={alertsRefreshKey}
            instructors={instructors}
            onJumpToInstructor={(id) => {
              const i = instructors.find((x) => x.id === id);
              if (i) setDashFor(i);
            }}
          />

          <Card className="instrument-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-orange-300">
                <Plane className="w-5 h-5" /> Direção de Instrução — Controlo de Instrutores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" data-testid="di-count-green">● {counts.green || 0} aptos</span>
                  <span className="px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid="di-count-amber">● {counts.amber || 0} em atenção</span>
                  <span className="px-2 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/40" data-testid="di-count-red">● {counts.red || 0} não aptos</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-cockpit-border bg-slate-900/50">
                    <Calendar className="w-3.5 h-3.5 text-slate-500" />
                    <Input
                      type="date"
                      value={asOf || todayStr()}
                      onChange={(e) => { const v = e.target.value; setAsOf(v === todayStr() ? "" : v); }}
                      className="w-36 h-7 text-xs border-0 bg-transparent p-0"
                      data-testid="di-panel-asof"
                      title="Data de cálculo (hoje por defeito)"
                    />
                    {asOf && <button type="button" onClick={() => setAsOf("")} className="text-[10px] uppercase text-slate-500 hover:text-orange-300" title="Limpar simulação">Hoje</button>}
                  </div>
                  <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="di-csv-export-btn">
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                  <Button size="sm" className="btn-cockpit" onClick={() => { setEditing(null); setCreating(true); }} data-testid="di-add-instructor-btn">
                    <Plus className="w-4 h-4 mr-1" /> Novo instrutor
                  </Button>
                  <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer ml-2">
                    <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-3 w-3 accent-orange-400" data-testid="di-show-inactive" />
                    Mostrar inactivos
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="py-10 text-center text-slate-500 text-sm">A carregar...</div>
          ) : instructors.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-cockpit-border rounded">Nenhum instrutor registado.</div>
          ) : (
            <div className="space-y-3" data-testid="di-instructors-list">
              {instructors.map((i) => (
                <InstructorCard
                  key={i.id}
                  instructor={i}
                  expanded={openIds.has(i.id)}
                  docCount={docCounts[i.id] || 0}
                  onToggle={() => setOpenIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(i.id)) next.delete(i.id); else next.add(i.id);
                    return next;
                  })}
                  onOpenLog={() => setOpenLog(i)}
                  onOpenDashboard={() => setDashFor(i)}
                  onEdit={() => { setEditing(i); setCreating(false); }}
                  onDelete={() => removeInstructor(i)}
                  onToggleActive={() => toggleInstructorActive(i)}
                  onDemoted={(id) => demoteToInstructor(id)}
                />
              ))}
            </div>
          )}

          {(creating || editing) && (
            <InstructorForm
              open
              instructor={editing}
              onClose={() => { setCreating(false); setEditing(null); }}
              onSaved={refresh}
            />
          )}

          {openLog && (
            <FlightLogDialog
              open
              instructor={openLog}
              onClose={() => setOpenLog(null)}
              onChanged={refresh}
              onOpenAudit={() => setAuditFor(openLog)}
            />
          )}

          {auditFor && (
            <AuditLogDialog
              open
              instructor={auditFor}
              onClose={() => setAuditFor(null)}
            />
          )}

          {dashFor && (
            <InstructorDashboardDialog
              open
              instructor={dashFor}
              asOf={asOf}
              onClose={() => setDashFor(null)}
              onChanged={refresh}
            />
          )}
        </TabsContent>

        <TabsContent value="pilots" className="mt-3">
          <PilotsPanel onInstructorCreated={refresh} />
        </TabsContent>

        <TabsContent value="reservations" className="mt-3">
          <ReservationsPanel />
        </TabsContent>
        <TabsContent value="aircraft" className="mt-3">
          <AircraftPanel />
        </TabsContent>

        <TabsContent value="notifications" className="mt-3">
          <EmailPanel />
        </TabsContent>

        <TabsContent value="audit" className="mt-3">
          <DiAuditPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
